import React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import Layout from '../components/Layout';
import AssistantModeSwitch from '../components/AssistantModeSwitch';
import { useAssistantEnabled } from '../hooks/useAssistantEnabled';
import { ticketFormPath } from '../lib/assistantLinks';

// Keeps the assistant-ui library out of the main bundle: it is fetched when
// the owner actually opens this page.
const AssistantChatView = React.lazy(() => import('../components/assistant/AssistantChatView'));

/** `?c=<id>`: a positive whole number, anything else is no conversation. */
function positiveInt(value: string | null): number | null {
  if (value === null || value.trim() === '') return null;
  const number = Number(value);
  return Number.isInteger(number) && number > 0 ? number : null;
}

/** "Problem melden" arrived here: a report thread, perhaps about one conversation. */
interface ReportIntent {
  conversationId: number | null;
  /** Changes with every new request, so the chat starts over for it. */
  key: number;
}

const CHAT_PLACEHOLDER =
  'flex min-h-[16rem] min-w-0 flex-1 items-center justify-center rounded-2xl border border-border bg-background/60';

function IntelligenceWorkspace() {
  const [searchParams, setSearchParams] = useSearchParams();
  const conversationId = positiveInt(searchParams.get('c'));
  const wantsReport = conversationId !== null || searchParams.get('report') === '1';

  // What the URL asked for is remembered here and handed to the chat; the URL
  // itself is cleaned up right away, so a reload does not start the thread
  // over (the thread keeps its binding in the session).
  const [intent, setIntent] = React.useState<ReportIntent | null>(() =>
    wantsReport ? { conversationId, key: 0 } : null,
  );

  React.useEffect(() => {
    if (!wantsReport) return;
    setIntent((previous) =>
      previous && previous.conversationId === conversationId
        ? previous
        : { conversationId, key: (previous?.key ?? 0) + 1 },
    );
    const next = new URLSearchParams(searchParams);
    next.delete('c');
    next.delete('report');
    setSearchParams(next, { replace: true });
  }, [wantsReport, conversationId, searchParams, setSearchParams]);

  return (
    <Layout>
      <div className="relative z-10 flex min-h-0 flex-1 flex-col gap-4 md:gap-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center">
          <div className="flex justify-start md:flex-1">
            <h1 className="text-3xl font-extrabold tracking-tight text-foreground md:text-[2rem] md:leading-10">
              TimeGlobe Intelligence
            </h1>
          </div>
          <div className="flex justify-center md:flex-1">
            <AssistantModeSwitch active="intelligence" />
          </div>
          <div className="hidden md:block md:flex-1" aria-hidden="true" />
        </div>

        <React.Suspense
          fallback={
            <div className={CHAT_PLACEHOLDER} role="status">
              <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden="true" />
              <span className="sr-only">Assistent wird geladen …</span>
            </div>
          }
        >
          <AssistantChatView
            key={intent?.key ?? 'thread'}
            reportMode={intent !== null}
            reportConversationId={intent?.conversationId ?? null}
          />
        </React.Suspense>
      </div>
    </Layout>
  );
}

/**
 * `/intelligence` — the assistant on a page of its own, so it has an address
 * (links from "Problem melden", from the assistant's answers, from the
 * sidebar) and survives navigation.
 *
 * Where the assistant is not switched on, nobody is stranded: a "Problem
 * melden" request lands on the ticket form it used to open, everything else
 * on the dashboard.
 */
const IntelligencePage: React.FC = () => {
  const { enabled, ready } = useAssistantEnabled();
  const [searchParams] = useSearchParams();

  // Until the server has answered, neither the page nor a redirect: no flash.
  if (!ready) {
    return (
      <Layout>
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4" role="status">
          <div className="relative">
            <div className="h-16 w-16 rounded-full border-4 border-primary/25" />
            <div className="absolute left-0 top-0 h-16 w-16 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
          <p className="animate-pulse text-sm text-muted-foreground">Assistent wird geladen...</p>
        </div>
      </Layout>
    );
  }

  if (!enabled) {
    const conversationId = positiveInt(searchParams.get('c'));
    if (conversationId !== null) return <Navigate to={ticketFormPath(conversationId)} replace />;
    if (searchParams.get('report') === '1') return <Navigate to={ticketFormPath()} replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return <IntelligenceWorkspace />;
};

export default IntelligencePage;
